import QuestionSequence from '@sleepstation/components-question-sequence'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useQuery } from 'urql'
import styled from 'styled-components'
import { map, prop } from 'ramda'
import UserContext from '../../../../context/UserContext'
import LoadOverlay from '../../../shared/LoadOverlay'
import { QuestionnaireQuery } from '../../../../graphql/documents'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const DBAS = `dbas`
const RANGE = `range`
const QUESTION = `question`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const mapScores = map(prop(`score`))

const transformOptions = (options) => {
  const scores = mapScores(options)
  const minScore = Math.min(...scores)
  const maxScore = Math.max(...scores)

  return {
    options: {
      max: {
        value: maxScore,
        label: `Strongly\nagree`,
      },
      min: {
        value: minScore,
        label: `Strongly\ndisagree`,
      },
      step: 1,
    },
    optionsDefaultValue: minScore,
  }
}

const transformStepObject = (step) => ({
  id: step.id,
  type: QUESTION,
  questionType: RANGE,
  question: step.contents.nodes[0].value,
  ...transformOptions(step.options.nodes),
  nextStep: {
    default: step.defaultNextStepId,
  },
  data: {
    survey: DBAS,
  },
})

const findNodeById = (nodes, id) => nodes.find((node) => node.id === id)

const orderSteps = ({ steps, firstStepId }) => {
  const orderedSteps = []
  const { nodes } = steps
  let nextStepId = firstStepId

  while (nextStepId) {
    const nextStep = findNodeById(nodes, nextStepId)
    orderedSteps.push(transformStepObject(nextStep))
    nextStepId = nextStep.defaultNextStepId
  }

  return orderedSteps
}

const determineStepResponses = (userData) =>
  Object.keys(userData).map((key) => {
    const answerString = userData[key].answer.toString()
    const answerInteger = parseInt(userData[key].answer, 10)
    const { step } = userData[key]

    return {
      stepId: step.id,
      chosenOptionScore: answerInteger,
      chosenOptionValue: answerString,
    }
  })

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div`
  position: relative;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const EpworthQuestionnaire = ({ onSuccess, onProgress }) => {
  const { currentEnrolment } = useContext(UserContext)
  const [result] = useQuery({
    query: QuestionnaireQuery,
    variables: {
      key: `dbas`,
    },
  })

  const { data, error } = result

  const handleComplete = ({ userData }) => {
    const responseParams = {
      params: {
        questionnaireId: data.questionnaire.id,
        enrolmentId: currentEnrolment.id,
        stepResponsesAttributes: determineStepResponses(userData),
      },
    }

    onSuccess(responseParams)
  }

  const handleProgress = (progressData) => {
    const { isComplete } = progressData

    onProgress(progressData)

    if (isComplete) {
      handleComplete(progressData)
    }
  }

  return !data || error ? (
    <LoadOverlay />
  ) : (
    <Layout>
      <QuestionSequence
        steps={orderSteps(data.questionnaire)}
        onProgress={handleProgress}
      />
    </Layout>
  )
}

EpworthQuestionnaire.propTypes = {
  onSuccess: PropTypes.func,
  onProgress: PropTypes.func,
}

EpworthQuestionnaire.defaultProps = {
  onSuccess: () => {},
  onProgress: () => {},
}

export default EpworthQuestionnaire
